/**
 * BRIGADE PHASE TAB COMPONENT
 * Phase 2 UI Component - Step 3
 *
 * This component adds phase configuration to the existing brigade dialog.
 * Features:
 * - Phase inheritance toggle (Division vs Custom)
 * - Display inherited phases with per-phase override capability (ENH-023)
 * - Edit custom phases
 * - Timeline visualization
 *
 * INTEGRATION: Add this as a new tab in the brigade configuration dialog
 *
 * Date: January 30, 2026
 * For: Dr. Tatar Radu MD - PSP/CRE Tool v3.0
 *
 * Version: 1.6.0 (April 10, 2026) - V4-013: Sub-phase Brigade Tactical Differentiation.
 *   Brigade phases (inherited or custom) can now be split into tactical sub-phases, each
 *   carrying its own mission_type (MAIN_EFFORT/URBAN/RIVER_CROSSING/…) and optional terrain
 *   override. Sub-phases are stored in phase_overrides[idx].sub_phases (inherited) or
 *   phases[idx].sub_phases (custom). buildEffectivePhases() propagates sub_phases so the
 *   calculator can read them per-day. UI: ExpandMoreIcon toggle in Actions column opens a
 *   Collapse panel with SP rows (days + mission type + terrain + delete). Duration sum chip
 *   validates sub-phases sum === parent phase duration.
 * Version: 1.5.0 (March 20, 2026) - ENH-023: Per-phase override for inherited phases.
 *   Brigade can override posture/mission/description on individual phases while still
 *   inheriting from the parent division. New state: phaseOverrides (index → {operational_posture,
 *   mission_type, description}). Duration override not permitted — parent timeline structure
 *   is preserved. UI: per-row Override/Reset action in inherited table; overridden rows
 *   highlighted amber. Effective phases (overrides merged into divisionPhases) written back
 *   to brigade.phases so the calculator reads correct values without schema changes.
 *   onUpdateBrigade now also writes phase_overrides to persist overrides through cascades.
 *   Top chip shows override count when partially overriding. Alert text updated.
 * Version: 1.4.0 (March 18, 2026) - APP_RULES: replace all bare emoji with icons.js constants.
 *   📋 → DATA_ICONS.CLIPBOARD  |  ✅ → STATUS_ICONS.GREEN_CHECK (×2)
 *   ⚙️ → UI_ICONS.SETTINGS    |  ⚠️ → STATUS_ICONS.YELLOW_WARNING
 *   ❌ → STATUS_ICONS.RED_X
 *   Imports added: STATUS_ICONS, UI_ICONS, DATA_ICONS from utils/icons.js
 * Version: 1.3.0 (March 14, 2026) - ENH-020: Specialized offensive postures added
 *   to brigade phase editor. Four postures were present in division editor but absent
 *   from brigade dropdown: URBAN_ASSAULT, RIVER_CROSSING, BREACH, INFILTRATION.
 *   All four added to POSTURE_TO_MISSION map (→ ATTACK) and to posture Select MenuItems.
 *   Auto-sync to mission_type = ATTACK via existing BUG-076 logic. Zero other changes.
 * Version: 1.2.0 (March 13, 2026) - BUG-096: TRANSIT and MARCH_TACTICAL postures missing
 *   from brigade phase posture dropdown. updatePhase() already mapped both to MOVEMENT
 *   (lines 118-121) but MenuItems were absent — user could not select them. Also added
 *   MOVEMENT to the mission_type Select so it is selectable for manual correction.
 * Version: 1.1.0 (March 13, 2026) - BUG-076: Posture/mission auto-sync in phase editing.
 *   updatePhase() now auto-sets mission_type when operational_posture changes, using the
 *   same postureToMissionType mapping as Tab2_Operations getMissionFromPosture(). User
 *   retains the ability to manually override mission_type independently after posture is set.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  AlertTitle,
  Stack,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import {
  STATUS_ICONS,
  UI_ICONS,
  DATA_ICONS,
} from '../utils/icons.js';
// eslint-disable-next-line no-unused-vars
import { OPERATION_TYPES, migrateSubPhase } from '../constants/missions.js';
import PhaseCasualtyControls from './PhaseCasualtyControls';
import { resolvePhaseProfile } from '../constants/phaseCasualtyTable';

/**
 * Brigade Phase Tab Component
 *
 * @param {Object} brigade - Brigade configuration object
 * @param {Array}  divisionPhases - Parent division's effective phases
 * @param {number} divisionNumber - Parent division number for display
 * @param {number} operationDurationDays - Total operation duration
 * @param {Function} onUpdateBrigade - Handler to update brigade config
 * @param {Function} onToggleInheritance - Handler for inheritance toggle
 */
const BrigadePhaseTab = ({
  brigade,
  divisionPhases = [],
  divisionNumber = 1,
  operationDurationDays = 10,
  onUpdateBrigade,
  onToggleInheritance
}) => {

  // Local state for phase editing
  const [usesDivisionPhases, setUsesDivisionPhases] = useState(brigade.uses_division_phases !== false);
  const [phases, setPhases] = useState(brigade.phases || []);
  // ENH-023: per-phase overrides — 0-based index → {operational_posture, mission_type, description}
  const [phaseOverrides, setPhaseOverrides] = useState(brigade.phase_overrides || {});
  // V4-013: tracks which phase rows have expanded sub-phase panel
  const [expandedSubPhases, setExpandedSubPhases] = useState({});

  // Sync with brigade prop changes
  // BUG-T04 fix: do NOT reset expandedSubPhases here — that collapsed the panel on every edit.
  // expandedSubPhases is UI-only state; reset it only when switching to a different brigade.
  useEffect(() => {
    setExpandedSubPhases({});
  }, [brigade.brigade_id]);

  useEffect(() => {
    setUsesDivisionPhases(brigade.uses_division_phases !== false);
    setPhases(brigade.phases || []);
    setPhaseOverrides(brigade.phase_overrides || {});
  }, [brigade.brigade_id, brigade.uses_division_phases, brigade.phases, brigade.phase_overrides]);

  // Calculate phase days from durations
  const calculatePhaseDays = (phasesArray) => {
    let currentDay = 1;
    return phasesArray.map(phase => {
      const startDay = currentDay;
      const endDay = currentDay + phase.duration_days - 1;
      currentDay = endDay + 1;
      return { ...phase, start_day: startDay, end_day: endDay };
    });
  };

  // BUG-076 FIX (March 13, 2026): posture → mission auto-sync.
  const POSTURE_TO_MISSION = {
    ATTACK:                'ATTACK',
    HASTY_ATTACK:          'ATTACK',
    DELIBERATE_ATTACK:     'ATTACK',
    EXPLOITATION:          'EXPLOITATION',
    PURSUIT:               'ATTACK',
    MOVEMENT_TO_CONTACT:   'ATTACK',
    MOBILE_DEFENSE:        'DEFEND',
    RETIREMENT:            'CONSOLIDATION',
    DEFENSE:               'DEFEND',
    HASTY_DEFENSE:         'DEFEND',
    DELIBERATE_DEFENSE:    'DEFEND',
    DELAY:                 'DEFEND',
    WITHDRAWAL:            'CONSOLIDATION',
    RETIRE:                'CONSOLIDATION',
    RESERVE:               'RESERVE',
    MARCH:                 'MOVEMENT',
    MARCH_TACTICAL:        'MOVEMENT',
    MARCH_ADMINISTRATIVE:  'MOVEMENT',
    MARCHING:              'MOVEMENT',
    TRANSIT:               'MOVEMENT',
    RECONSTITUTION:        'SUPPORTING',
    SUPPORT_OPERATIONS:    'SUPPORTING',
    CONSOLIDATION:         'CONSOLIDATION',
    // ENH-020 (March 14, 2026): Specialized offensive postures
    URBAN_ASSAULT:         'ATTACK',
    RIVER_CROSSING:        'ATTACK',
    BREACH:                'ATTACK',
    INFILTRATION:          'ATTACK'
  };

  // V5-012: Brigade role chip options for per-phase role designation.
  // Clicking a chip sets phase_overrides[idx].brigade_role (inherited) or phase.brigade_role (custom).
  // Calculator reads this before falling back to the static Tab1 mission_multiplier.
  const BRIGADE_ROLE_OPTIONS = [
    { value: 'MAIN_EFFORT',      label: 'Main Effort (frontal)',  color: 'error'   },
    { value: 'SUPPORTING',       label: 'Supporting (frontal)',   color: 'primary' },
    { value: 'RESERVE',          label: 'Reserve (near zero)',      color: 'default' },
    { value: 'EXPLOITATION',     label: 'Exploitation (flank ×0.75)', color: 'warning' },
    { value: 'ECONOMY_OF_FORCE', label: 'Economy (limited ×0.60)',      color: 'info'    },
  ];

  // ── Custom-phase handlers ──────────────────────────────────────────────────

  const updatePhase = (phaseIndex, field, value) => {
    const newPhases = [...phases];
    const updatedPhase = { ...newPhases[phaseIndex], [field]: value };
    if (field === 'operational_posture') {
      updatedPhase.mission_type = POSTURE_TO_MISSION[value] || 'DEFEND';
    }
    newPhases[phaseIndex] = updatedPhase;
    const recalculated = calculatePhaseDays(newPhases);
    setPhases(recalculated);
    onUpdateBrigade({ ...brigade, phases: recalculated });
  };

  const addPhase = () => {
    const newPhase = {
      phase_number: phases.length + 1,
      duration_days: 1,
      operational_posture: 'DELIBERATE_DEFENSE',
      mission_type: 'DEFEND',
      description: `Phase ${phases.length + 1}`
    };
    const newPhases = calculatePhaseDays([...phases, newPhase]);
    setPhases(newPhases);
    onUpdateBrigade({ ...brigade, phases: newPhases });
  };

  const deletePhase = (phaseIndex) => {
    const filtered = phases.filter((_, i) => i !== phaseIndex);
    const renumbered = filtered.map((p, i) => ({ ...p, phase_number: i + 1 }));
    const recalculated = calculatePhaseDays(renumbered);
    setPhases(recalculated);
    onUpdateBrigade({ ...brigade, phases: recalculated });
  };

  // Handle inheritance toggle
  const handleToggleInheritance = (checked) => {
    setUsesDivisionPhases(checked);
    onToggleInheritance(brigade.brigade_id, checked);
  };

  // ── ENH-023: Per-phase override handlers ──────────────────────────────────

  /**
   * Build effective phases: parent phases with overrides applied.
   * Duration is never overridden — parent timeline structure is preserved.
   */
  const buildEffectivePhases = (parentPhases, overrides) =>
    parentPhases.map((phase, index) => {
      const ov = overrides[index];
      if (!ov) return { ...phase };
      return {
        ...phase,
        operational_posture: ov.operational_posture ?? phase.operational_posture,
        mission_type:        ov.mission_type        ?? phase.mission_type,
        description:         ov.description         ?? phase.description,
        // 2026-10-13 (Tab 2 plan, step 3b): casualty profile of the phase (constants/phaseCasualtyTable.js)
        resistance:           ov.resistance           ?? phase.resistance,
        enemy_strength_ratio: ov.enemy_strength_ratio ?? phase.enemy_strength_ratio,
        maneuver_group:       ov.maneuver_group       ?? phase.maneuver_group,
        special_condition:    ov.special_condition    ?? phase.special_condition,
        foothold_day:         ov.foothold_day         ?? phase.foothold_day,
        weather:              ov.weather              ?? phase.weather,   // R2-18
        advance_km_day:     ov.advance_km_day     ?? phase.advance_km_day,   // R2-30: advance of the brigade in this phase, km per day
        // V4-013: propagate sub_phases so calculator can read per-day tactical missions
        sub_phases:          ov.sub_phases           ?? phase.sub_phases ?? [],
      };
    });

  /** Seed override from current parent phase values, then commit. */
  const addOverride = (index, phase) => {
    const newOverrides = {
      ...phaseOverrides,
      [index]: {
        operational_posture: phase.operational_posture,
        mission_type:        phase.mission_type,
        description:         phase.description || ''
      }
    };
    setPhaseOverrides(newOverrides);
    onUpdateBrigade({
      ...brigade,
      phase_overrides: newOverrides,
      phases: buildEffectivePhases(divisionPhases, newOverrides)
    });
  };

  /** Update a single field inside an existing override. */
  const updateOverride = (index, field, value) => {
    const existing = phaseOverrides[index] || {};
    const updated = { ...existing, [field]: value };
    if (field === 'operational_posture') {
      updated.mission_type = POSTURE_TO_MISSION[value] || 'DEFEND';
    }
    const newOverrides = { ...phaseOverrides, [index]: updated };
    setPhaseOverrides(newOverrides);
    onUpdateBrigade({
      ...brigade,
      phase_overrides: newOverrides,
      phases: buildEffectivePhases(divisionPhases, newOverrides)
    });
  };

  /** Remove override for a phase — reverts to inherited parent value. */
  const removeOverride = (index) => {
    const newOverrides = { ...phaseOverrides };
    delete newOverrides[index];
    setPhaseOverrides(newOverrides);
    onUpdateBrigade({
      ...brigade,
      phase_overrides: newOverrides,
      phases: buildEffectivePhases(divisionPhases, newOverrides)
    });
  };

  // ── V4-013: Sub-phase management ─────────────────────────────────────────

  const toggleSubPhaseExpand = (phaseIdx) => {
    setExpandedSubPhases(prev => ({ ...prev, [phaseIdx]: !prev[phaseIdx] }));
  };

  const _getSubPhases = (phaseIdx, isInherited) => {
    if (isInherited) return phaseOverrides[phaseIdx]?.sub_phases || [];
    return phases[phaseIdx]?.sub_phases || [];
  };

  const _setSubPhases = (phaseIdx, newSPs, isInherited) => {
    if (isInherited) {
      updateOverride(phaseIdx, 'sub_phases', newSPs);
    } else {
      updatePhase(phaseIdx, 'sub_phases', newSPs);
    }
  };

  // BUG-T02 fix: parent phases have mission_type values like 'ATTACK','DEFEND','MOVEMENT'
  // from posture auto-sync — these are not in the sub-phase Select options, causing blank display.
  // Normalize to the nearest valid sub-phase mission value.
  const normalizeSubPhaseMission = (m) => {
    const VALID_SP = ['MAIN_EFFORT','SUPPORTING','URBAN','RIVER_CROSSING',
                      'EXPLOITATION','ECONOMY_OF_FORCE','RESERVE'];
    if (!m || VALID_SP.includes(m)) return m || 'SUPPORTING';
    const MAP = {
      ATTACK: 'SUPPORTING', HASTY_ATTACK: 'SUPPORTING', DELIBERATE_ATTACK: 'MAIN_EFFORT',
      DEFEND: 'ECONOMY_OF_FORCE', HASTY_DEFENSE: 'ECONOMY_OF_FORCE',
      DELIBERATE_DEFENSE: 'ECONOMY_OF_FORCE', DELAY: 'ECONOMY_OF_FORCE',
      MOVEMENT: 'SUPPORTING', MARCH: 'SUPPORTING', MARCH_TACTICAL: 'SUPPORTING',
      CONSOLIDATION: 'RESERVE', RESERVE: 'RESERVE', WITHDRAWAL: 'RESERVE',
      RETIRE: 'RESERVE', PURSUIT: 'EXPLOITATION',
    };
    return MAP[m] ?? 'SUPPORTING';
  };

  const initSubPhases = (phaseIdx, phaseDuration, defaultMission, isInherited) => {
    const normMission = normalizeSubPhaseMission(defaultMission);
    // OBS-S102-007: new format — role + operation_type instead of single mission_type
    const OP_TYPE_ONLY = ['URBAN', 'RIVER_CROSSING'];
    _setSubPhases(phaseIdx, [{
      sub_phase_number: 1,
      duration_days: phaseDuration,
      role:           OP_TYPE_ONLY.includes(normMission) ? 'SUPPORTING' : normMission,
      operation_type: OP_TYPE_ONLY.includes(normMission) ? normMission : null,
      mission_type:   normMission, // BC field
      terrain: null
    }], isInherited);
    setExpandedSubPhases(prev => ({ ...prev, [phaseIdx]: true }));
  };

  const addSubPhase = (phaseIdx, defaultMission, isInherited) => {
    const current = _getSubPhases(phaseIdx, isInherited);
    const normMission = normalizeSubPhaseMission(defaultMission);
    const OP_TYPE_ONLY = ['URBAN', 'RIVER_CROSSING'];
    _setSubPhases(phaseIdx, [
      ...current,
      {
        sub_phase_number: current.length + 1,
        duration_days: 1,
        role:           OP_TYPE_ONLY.includes(normMission) ? 'SUPPORTING' : normMission,
        operation_type: OP_TYPE_ONLY.includes(normMission) ? normMission : null,
        mission_type:   normMission, // BC field
        terrain: null
      }
    ], isInherited);
  };

  const removeSubPhase = (phaseIdx, spIdx, isInherited) => {
    const updated = _getSubPhases(phaseIdx, isInherited)
      .filter((_, i) => i !== spIdx)
      .map((sp, i) => ({ ...sp, sub_phase_number: i + 1 }));
    _setSubPhases(phaseIdx, updated, isInherited);
  };

  // OBS-S102-007: multi-field update (role + operation_type changes need atomic write)
  const updateSubPhaseFields = (phaseIdx, spIdx, fields, isInherited) => {
    const updated = _getSubPhases(phaseIdx, isInherited)
      .map((sp, i) => i === spIdx ? { ...migrateSubPhase(sp), ...fields } : migrateSubPhase(sp));
    _setSubPhases(phaseIdx, updated, isInherited);
  };

  const disableSubPhases = (phaseIdx, isInherited) => {
    _setSubPhases(phaseIdx, [], isInherited);
    setExpandedSubPhases(prev => ({ ...prev, [phaseIdx]: false }));
  };

  /** Renders the sub-phase editor collapse panel for a given phase. */
  const renderSubPhasePanel = (phaseIdx, phaseDuration, parentMission, isInherited) => {
    const sps = _getSubPhases(phaseIdx, isInherited);
    const hasSubPhases = sps.length > 0;
    const sumDays = sps.reduce((s, sp) => s + (sp.duration_days || 0), 0);
    const sumValid = sumDays === phaseDuration;

    return (
      <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mt: 0.5, mb: 0.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary">
            Tactical sub-phases — {phaseDuration} day{phaseDuration !== 1 ? 's' : ''} to allocate
          </Typography>
          {hasSubPhases ? (
            <Stack direction="row" spacing={0.5}>
              <Button size="small" startIcon={<AddIcon />} variant="outlined"
                onClick={() => addSubPhase(phaseIdx, parentMission, isInherited)}>
                Add
              </Button>
              <Button size="small" color="warning" variant="text"
                onClick={() => disableSubPhases(phaseIdx, isInherited)}>
                Disable
              </Button>
            </Stack>
          ) : (
            <Button size="small" startIcon={<AddIcon />} variant="contained" color="primary"
              onClick={() => initSubPhases(phaseIdx, phaseDuration, parentMission, isInherited)}>
              Enable Sub-phases
            </Button>
          )}
        </Stack>

        {hasSubPhases && (
          <>
            {/* BUG-T03 / BUG-S102-025 FIX: show over/under delta + proportional-scaling note */}
            <Chip
              color={sumValid ? 'success' : 'warning'}
              size="small"
              label={sumValid
                ? `${sumDays}d allocated — exact match ✓`
                : sumDays < phaseDuration
                  ? `${sumDays}d / ${phaseDuration}d — ${phaseDuration - sumDays}d short (proportional scaling applied)`
                  : `${sumDays}d / ${phaseDuration}d — ${sumDays - phaseDuration}d over (proportional scaling applied)`}
              sx={{ mb: 1 }}
            />
            {sps.map((sp, spIdx) => {
              // OBS-S102-007: migrate old single-field sub-phases on display
              const msp = migrateSubPhase(sp);
              // 2026-10-13: the factor shown is the casualty table factor at moderate resistance (form x condition x group), not the old multiplier
              const combined   = (() => {
                try {
                  return resolvePhaseProfile({ posture: 'DELIBERATE_DEFENSE', subPhase: msp, dayInPhase: 1, resistanceIndex: 1 }).total_factor;
                } catch (e) {
                  return null;
                }
              })();
              const showCombined = msp.operation_type != null && combined !== null;
              return (
              <Box key={spIdx} sx={{ mb: 0.75, p: 0.75, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                {/* Row 1: SP# | Days | Role */}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ minWidth: 28, fontWeight: 'bold', color: 'primary.main' }}>
                    SP{msp.sub_phase_number}
                  </Typography>
                  <TextField
                    type="number"
                    size="small"
                    label="Days"
                    value={msp.duration_days}
                    onChange={(e) => updateSubPhaseFields(phaseIdx, spIdx, { duration_days: Math.max(1, parseInt(e.target.value, 10) || 1) }, isInherited)}
                    sx={{ width: 72 }}
                    inputProps={{ min: 1 }}
                  />
                  {/* Selector 1: Role */}
                  <FormControl size="small" sx={{ minWidth: 195 }}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Role</InputLabel>
                    <Select
                      label="Role"
                      value={msp.role ?? 'SUPPORTING'}
                      onChange={(e) => updateSubPhaseFields(phaseIdx, spIdx, {
                        role: e.target.value,
                        mission_type: e.target.value // BC field
                      }, isInherited)}
                    >
                      <MenuItem value="MAIN_EFFORT">Main Effort (frontal)</MenuItem>
                      <MenuItem value="SUPPORTING">Supporting (frontal)</MenuItem>
                      <MenuItem value="EXPLOITATION">Exploitation (flank / indirect ×0.75)</MenuItem>
                      <MenuItem value="ECONOMY_OF_FORCE">Economy of Force (limited objective ×0.60)</MenuItem>
                      <MenuItem value="FEINT">Feint / Demonstration (×0.40)</MenuItem>
                      <MenuItem value="FIXING">Fixing (×0.40)</MenuItem>
                      <MenuItem value="RESERVE">Reserve (near zero)</MenuItem>
                    </Select>
                  </FormControl>
                  {/* Selector 2: Operation Type */}
                  <FormControl size="small" sx={{ minWidth: 210 }}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Operation Type</InputLabel>
                    <Select
                      label="Operation Type"
                      value={msp.operation_type ?? ''}
                      onChange={(e) => updateSubPhaseFields(phaseIdx, spIdx, { operation_type: e.target.value || null }, isInherited)}
                      displayEmpty
                    >
                      <MenuItem value=""><em>(none — inherits phase posture)</em></MenuItem>
                      <ListSubheader sx={{ fontSize: '0.7rem', lineHeight: '24px' }}>Offensive</ListSubheader>
                      <MenuItem value="DELIBERATE_ATTACK">Deliberate Attack</MenuItem>
                      <MenuItem value="HASTY_ATTACK">Hasty Attack</MenuItem>
                      <MenuItem value="MOVEMENT_TO_CONTACT">Movement to Contact</MenuItem>
                      <MenuItem value="URBAN">Urban Operations (MOUT)</MenuItem>
                      <MenuItem value="RIVER_CROSSING">River Crossing (assault, then bridgehead)</MenuItem>
                      <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                      <MenuItem value="PURSUIT">Pursuit</MenuItem>
                      <MenuItem value="AIRBORNE_ASSAULT">Airborne / Air Assault (until link-up)</MenuItem>
                      <ListSubheader sx={{ fontSize: '0.7rem', lineHeight: '24px' }}>Defensive</ListSubheader>
                      <MenuItem value="DEFEND">Defend (Prepared)</MenuItem>
                      <MenuItem value="HASTY_DEFENSE">Hasty Defense</MenuItem>
                      <MenuItem value="MOBILE_DEFENSE">Mobile Defense</MenuItem>
                      <MenuItem value="DELAY">Delay</MenuItem>
                      <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                      <MenuItem value="RETIREMENT">Retirement</MenuItem>
                      <ListSubheader sx={{ fontSize: '0.7rem', lineHeight: '24px' }}>Movement</ListSubheader>
                      <MenuItem value="TACTICAL_MARCH">Tactical March</MenuItem>
                      <MenuItem value="ADMINISTRATIVE_MARCH">Administrative March</MenuItem>
                    </Select>
                  </FormControl>
                  {/* Selector 3: Terrain */}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Terrain</InputLabel>
                    <Select
                      label="Terrain"
                      value={msp.terrain ?? ''}
                      onChange={(e) => updateSubPhaseFields(phaseIdx, spIdx, { terrain: e.target.value || null }, isInherited)}
                      displayEmpty
                    >
                      <MenuItem value=""><em>(Scenario terrain)</em></MenuItem>
                      <MenuItem value="PLAINS">Plains / Flatland</MenuItem>
                      <MenuItem value="ROLLING_HILLS">Rolling Hills</MenuItem>
                      <MenuItem value="MOUNTAIN">Mountainous</MenuItem>
                      <MenuItem value="FOREST">Forested</MenuItem>
                      <MenuItem value="URBAN">Urban / City</MenuItem>
                      <MenuItem value="URBAN_COMBAT">Urban Combat Zone</MenuItem>
                      <MenuItem value="DESERT">Desert / Arid</MenuItem>
                      <MenuItem value="SWAMP_WETLAND">Swamp / Wetland</MenuItem>
                      <MenuItem value="COASTAL">Coastal / Littoral</MenuItem>
                    </Select>
                  </FormControl>
                  {/* Combined multiplier chip */}
                  {showCombined && (
                    <Chip
                      size="small"
                      label={`Table factor ×${combined.toFixed(2)} (moderate)`}
                      color={combined >= 2.0 ? 'error' : combined >= 1.3 ? 'warning' : combined <= 0.5 ? 'info' : 'default'}
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  )}
                  <IconButton size="small" color="error" sx={{ ml: 'auto' }}
                    onClick={() => removeSubPhase(phaseIdx, spIdx, isInherited)}
                    disabled={sps.length <= 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
              );
            })}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              V4-013: Multipliers stack on brigade tactical role (Tab 1). Applied per-day in calculator.
              If day totals don't match exactly, the calculator uses proportional scaling — every day still maps to a sub-phase.
              Set totals to exactly {phaseDuration}d for a precise day-boundary split.
            </Typography>
          </>
        )}
      </Box>
    );
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const activePhasesArray = usesDivisionPhases ? divisionPhases : phases;
  const totalPhaseDays = activePhasesArray.reduce((sum, p) => sum + p.duration_days, 0);
  const phaseDaysValid   = totalPhaseDays === operationDurationDays;
  const phaseDaysError   = totalPhaseDays > operationDurationDays;
  const phaseDaysWarning = totalPhaseDays < operationDurationDays && activePhasesArray.length > 0;

  const overrideCount = Object.keys(phaseOverrides).length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <TimelineIcon color="primary" />
        <Typography variant="h6">Operational Phases</Typography>
        <Chip
          label={
            usesDivisionPhases
              ? (overrideCount > 0
                  ? `${overrideCount} Override${overrideCount > 1 ? 's' : ''}`
                  : 'Inherited')
              : 'Custom'
          }
          color={usesDivisionPhases ? (overrideCount > 0 ? 'warning' : 'success') : 'warning'}
          size="small"
        />
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>{DATA_ICONS.CLIPBOARD} Phase Configuration</AlertTitle>
        Brigades can inherit phases from their parent division or define custom phases for
        detached operations. When inheriting, individual phases can be overridden
        (posture/mission/description) while keeping the parent timeline structure.
        Duration cannot be changed via override.
      </Alert>

      {/* Inheritance Toggle */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={usesDivisionPhases}
              onChange={(e) => handleToggleInheritance(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Stack>
              <Typography variant="subtitle1">
                {usesDivisionPhases
                  ? `${STATUS_ICONS.GREEN_CHECK} Inherit Phases from Division ${divisionNumber}`
                  : `${UI_ICONS.SETTINGS} Use Custom Brigade Phases`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {usesDivisionPhases
                  ? `This brigade follows Division ${divisionNumber} operational phases`
                  : 'This brigade has independent operational phases (detached mission)'}
              </Typography>
            </Stack>
          }
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Phase Display */}
      {usesDivisionPhases ? (

        // ── INHERITED PHASES — read-only except for per-phase overrides (ENH-023)
        <Box>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Inherited Phases from Division {divisionNumber}
            </Typography>
            <Chip label={`${divisionPhases.length} phases`} size="small" color="success" />
            {overrideCount > 0 && (
              <Chip
                label={`${overrideCount} overridden`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Stack>

          {divisionPhases.length === 0 ? (
            <Alert severity="info">
              Division {divisionNumber} has not defined phases yet. This brigade will
              automatically receive phases when the division configures them.
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }} icon={false}>
                <Typography variant="body2">
                  Click <strong>Override</strong> on any row to change posture, mission, or
                  description for that phase only. Duration is locked to the parent timeline.
                  Click <strong>Reset</strong> to revert to the inherited value.
                </Typography>
              </Alert>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Phase</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Days</TableCell>
                      <TableCell>Posture</TableCell>
                      <TableCell>Mission</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {divisionPhases.map((phase, index) => {
                      const isOverridden = Boolean(phaseOverrides[index]);
                      const ov = phaseOverrides[index] || {};
                      return (
                        <React.Fragment key={index}>
                        <TableRow
                          sx={{ bgcolor: isOverridden ? 'warning.50' : 'inherit' }}
                        >
                          {/* Phase chip + brigade_role summary */}
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                              <Chip
                                label={`Phase ${phase.phase_number}`}
                                size="small"
                                color={isOverridden ? 'warning' : 'success'}
                              />
                              {/* V5-012: show role badge in collapsed row */}
                              {ov.brigade_role && (() => {
                                const roleOpt = BRIGADE_ROLE_OPTIONS.find(o => o.value === ov.brigade_role);
                                return roleOpt ? (
                                  <Chip
                                    label={roleOpt.value === 'ECONOMY_OF_FORCE' ? 'EcF' : roleOpt.value.replace('_', ' ').split(' ').map(w => w[0]).join('')}
                                    size="small"
                                    color={roleOpt.color}
                                    variant="filled"
                                    title={roleOpt.label}
                                  />
                                ) : null;
                              })()}
                            </Stack>
                          </TableCell>
                          {/* Duration — always locked */}
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {phase.duration_days} days
                            </Typography>
                          </TableCell>
                          {/* Start–End */}
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {phase.start_day}–{phase.end_day}
                            </Typography>
                          </TableCell>
                          {/* Posture — editable when overridden */}
                          <TableCell>
                            {isOverridden ? (
                              <FormControl size="small" sx={{ minWidth: 160 }}>
                                <Select
                                  value={ov.operational_posture}
                                  onChange={(e) =>
                                    updateOverride(index, 'operational_posture', e.target.value)
                                  }
                                >
                                  <MenuItem value="DELIBERATE_ATTACK">Deliberate Attack</MenuItem>
                                  <MenuItem value="HASTY_ATTACK">Hasty Attack</MenuItem>
                                  <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                                  <MenuItem value="DELIBERATE_DEFENSE">Deliberate Defense</MenuItem>
                                  <MenuItem value="HASTY_DEFENSE">Hasty Defense</MenuItem>
                                  <MenuItem value="MOVEMENT_TO_CONTACT">Movement to Contact</MenuItem>
                                  <MenuItem value="PURSUIT">Pursuit</MenuItem>
                                  <MenuItem value="MOBILE_DEFENSE">Mobile Defense</MenuItem>
                                  <MenuItem value="DELAY">Delay</MenuItem>
                                  <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                                  <MenuItem value="RETIREMENT">Retirement (out of contact)</MenuItem>
                                  <MenuItem value="CONSOLIDATION">Consolidation</MenuItem>
                                  <MenuItem value="RESERVE">Reserve</MenuItem>
                                  <MenuItem value="MARCH_TACTICAL">Tactical March</MenuItem>
                                  <MenuItem value="TRANSIT">Transit</MenuItem>
                                  <MenuItem value="URBAN_ASSAULT">Urban Assault</MenuItem>
                                  <MenuItem value="RIVER_CROSSING">River Crossing</MenuItem>
                                  <MenuItem value="BREACH">Breach</MenuItem>
                                  <MenuItem value="INFILTRATION">Infiltration</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <Typography variant="body2">{phase.operational_posture}</Typography>
                            )}
                          </TableCell>
                          {/* Mission — editable when overridden */}
                          <TableCell>
                            {isOverridden ? (
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={ov.mission_type}
                                  onChange={(e) =>
                                    updateOverride(index, 'mission_type', e.target.value)
                                  }
                                >
                                  <MenuItem value="ATTACK">Attack</MenuItem>
                                  <MenuItem value="DEFEND">Defend</MenuItem>
                                  <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                                  <MenuItem value="CONSOLIDATION">Consolidation</MenuItem>
                                  <MenuItem value="RESERVE">Reserve</MenuItem>
                                  <MenuItem value="MOVEMENT">Movement</MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              <Typography variant="body2">{phase.mission_type}</Typography>
                            )}
                          </TableCell>
                          {/* Description — editable when overridden */}
                          <TableCell>
                            {isOverridden ? (
                              <TextField
                                size="small"
                                value={ov.description ?? phase.description ?? ''}
                                onChange={(e) =>
                                  updateOverride(index, 'description', e.target.value)
                                }
                                placeholder="Phase description"
                                sx={{ minWidth: 180 }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {phase.description || '–'}
                              </Typography>
                            )}
                          </TableCell>
                          {/* Override / Reset + Sub-phase toggle */}
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                              {isOverridden && (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleSubPhaseExpand(index)}
                                  color={_getSubPhases(index, true).length > 0 ? 'primary' : 'default'}
                                  title="Toggle sub-phase editor"
                                >
                                  <ExpandMoreIcon
                                    sx={{
                                      transform: expandedSubPhases[index] ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.2s'
                                    }}
                                  />
                                </IconButton>
                              )}
                              {isOverridden ? (
                                <Button
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  onClick={() => removeOverride(index)}
                                >
                                  Reset
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => addOverride(index, phase)}
                                >
                                  Override
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                        {/* V5-012: Brigade role chip row — visible when phase is overridden */}
                        {isOverridden && (
                          <TableRow key={`${index}-role`}>
                            <TableCell
                              colSpan={7}
                              sx={{ pt: 0.5, pb: 0.5, bgcolor: 'warning.50', borderBottom: 'none' }}
                            >
                              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 88, fontWeight: 500 }}>
                                  Brigade Role:
                                </Typography>
                                {BRIGADE_ROLE_OPTIONS.map(opt => {
                                  const selected = (ov.brigade_role ?? null) === opt.value;
                                  return (
                                    <Chip
                                      key={opt.value}
                                      label={opt.label}
                                      size="small"
                                      color={selected ? opt.color : 'default'}
                                      variant={selected ? 'filled' : 'outlined'}
                                      onClick={() => updateOverride(index, 'brigade_role', selected ? null : opt.value)}
                                      sx={{ cursor: 'pointer', fontWeight: selected ? 'bold' : 'normal' }}
                                    />
                                  );
                                })}
                                {ov.brigade_role && (
                                  <Chip
                                    label="Clear"
                                    size="small"
                                    variant="outlined"
                                    onClick={() => updateOverride(index, 'brigade_role', null)}
                                    sx={{ cursor: 'pointer' }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )}
                        {/* 2026-10-13: casualty profile row (resistance, enemy ratio, maneuver group, special condition) */}
                        {isOverridden && (
                          <TableRow key={`${index}-profile`}>
                            <TableCell colSpan={7} sx={{ pt: 0.5, pb: 0.75, bgcolor: 'warning.50', borderBottom: 'none' }}>
                              <PhaseCasualtyControls
                                phase={{ ...phase, ...ov }}
                                onChange={(field, value) => updateOverride(index, field, value)}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                        {/* V4-013: Sub-phase editor row */}
                        {isOverridden && (
                          <TableRow key={`${index}-sp`}>
                            <TableCell
                              colSpan={7}
                              sx={{ pt: 0, pb: expandedSubPhases[index] ? 1 : 0, borderBottom: expandedSubPhases[index] ? undefined : 'none' }}
                            >
                              <Collapse in={Boolean(expandedSubPhases[index])} timeout="auto" unmountOnExit>
                                {/* BUG-S102-005 FIX: brigade_role chip takes precedence over posture-based
                                    mission_type when defaulting new sub-phases. ov.brigade_role is the
                                    user's explicit Main Effort / Supporting / etc. designation. */}
                                {renderSubPhasePanel(index, phase.duration_days, ov.brigade_role || ov.mission_type || phase.mission_type, true)}
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Timeline — overridden phases shown with darker/outlined bar */}
              <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Timeline (Total: {operationDurationDays} days)
                  {overrideCount > 0 && ' — outlined bars have brigade overrides'}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                  {divisionPhases.map((phase, index) => {
                    const isOverridden = Boolean(phaseOverrides[index]);
                    return (
                      <Box
                        key={index}
                        sx={{
                          flex: phase.duration_days,
                          height: 40,
                          bgcolor: isOverridden
                            ? `hsl(${index * 60}, 80%, 42%)`
                            : `hsl(${index * 60}, 70%, 60%)`,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          outline: isOverridden ? '2px solid #f57c00' : 'none',
                          outlineOffset: '1px',
                        }}
                      >
                        P{phase.phase_number}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </>
          )}
        </Box>

      ) : (

        // ── CUSTOM PHASES — Editable
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Custom Brigade Phases
            </Typography>
            <Button startIcon={<AddIcon />} onClick={addPhase} variant="outlined" size="small">
              Add Phase
            </Button>
          </Stack>

          {phases.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No custom phases configured. Add phases to define this brigade&apos;s independent
              timeline.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Phase</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Days</TableCell>
                      <TableCell>Posture</TableCell>
                      <TableCell>Mission</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {phases.map((phase, index) => (
                      <React.Fragment key={index}>
                      <TableRow>
                        <TableCell>
                          <Chip label={`Phase ${phase.phase_number}`} size="small" color="warning" />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={phase.duration_days}
                            onChange={(e) => updatePhase(index, 'duration_days', Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">days</InputAdornment>
                            }}
                            sx={{ width: 100 }}
                            inputProps={{ min: 1 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {phase.start_day}–{phase.end_day}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={phase.operational_posture}
                              onChange={(e) => updatePhase(index, 'operational_posture', e.target.value)}
                            >
                              <MenuItem value="DELIBERATE_ATTACK">Deliberate Attack</MenuItem>
                              <MenuItem value="HASTY_ATTACK">Hasty Attack</MenuItem>
                              <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                              <MenuItem value="DELIBERATE_DEFENSE">Deliberate Defense</MenuItem>
                              <MenuItem value="HASTY_DEFENSE">Hasty Defense</MenuItem>
                              <MenuItem value="MOVEMENT_TO_CONTACT">Movement to Contact</MenuItem>
                              <MenuItem value="PURSUIT">Pursuit</MenuItem>
                              <MenuItem value="MOBILE_DEFENSE">Mobile Defense</MenuItem>
                              <MenuItem value="DELAY">Delay</MenuItem>
                              <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                              <MenuItem value="RETIREMENT">Retirement (out of contact)</MenuItem>
                              <MenuItem value="CONSOLIDATION">Consolidation</MenuItem>
                              <MenuItem value="RESERVE">Reserve</MenuItem>
                              {/* BUG-096 FIX: TRANSIT and MARCH_TACTICAL were absent */}
                              <MenuItem value="MARCH_TACTICAL">Tactical March</MenuItem>
                              <MenuItem value="TRANSIT">Transit</MenuItem>
                              {/* ENH-020: Specialized offensive postures */}
                              <MenuItem value="URBAN_ASSAULT">Urban Assault</MenuItem>
                              <MenuItem value="RIVER_CROSSING">River Crossing</MenuItem>
                              <MenuItem value="BREACH">Breach</MenuItem>
                              <MenuItem value="INFILTRATION">Infiltration</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={phase.mission_type}
                              onChange={(e) => updatePhase(index, 'mission_type', e.target.value)}
                            >
                              <MenuItem value="ATTACK">Attack</MenuItem>
                              <MenuItem value="DEFEND">Defend</MenuItem>
                              <MenuItem value="EXPLOITATION">Exploitation</MenuItem>
                              <MenuItem value="CONSOLIDATION">Consolidation</MenuItem>
                              <MenuItem value="RESERVE">Reserve</MenuItem>
                              {/* BUG-096 FIX: MOVEMENT added */}
                              <MenuItem value="MOVEMENT">Movement</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={phase.description || ''}
                            onChange={(e) => updatePhase(index, 'description', e.target.value)}
                            placeholder="Phase description"
                            sx={{ minWidth: 200 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => toggleSubPhaseExpand(index)}
                              color={_getSubPhases(index, false).length > 0 ? 'primary' : 'default'}
                              title="Toggle sub-phase editor"
                            >
                              <ExpandMoreIcon
                                sx={{
                                  transform: expandedSubPhases[index] ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s'
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => deletePhase(index)}
                              disabled={phases.length === 1}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {/* V5-012: Brigade role chip row for custom phases */}
                      <TableRow key={`${index}-role`}>
                        <TableCell
                          colSpan={7}
                          sx={{ pt: 0.5, pb: 0.5, bgcolor: 'action.hover', borderBottom: 'none' }}
                        >
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 88, fontWeight: 500 }}>
                              Brigade Role:
                            </Typography>
                            {BRIGADE_ROLE_OPTIONS.map(opt => {
                              const selected = (phase.brigade_role ?? null) === opt.value;
                              return (
                                <Chip
                                  key={opt.value}
                                  label={opt.label}
                                  size="small"
                                  color={selected ? opt.color : 'default'}
                                  variant={selected ? 'filled' : 'outlined'}
                                  onClick={() => updatePhase(index, 'brigade_role', selected ? null : opt.value)}
                                  sx={{ cursor: 'pointer', fontWeight: selected ? 'bold' : 'normal' }}
                                />
                              );
                            })}
                            {phase.brigade_role && (
                              <Chip
                                label="Clear"
                                size="small"
                                variant="outlined"
                                onClick={() => updatePhase(index, 'brigade_role', null)}
                                sx={{ cursor: 'pointer' }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {/* V4-013: Sub-phase editor row */}
                      <TableRow key={`${index}-sp`}>
                        <TableCell
                          colSpan={7}
                          sx={{ pt: 0, pb: expandedSubPhases[index] ? 1 : 0, borderBottom: expandedSubPhases[index] ? undefined : 'none' }}
                        >
                          <Collapse in={Boolean(expandedSubPhases[index])} timeout="auto" unmountOnExit>
                            {/* BUG-S102-005 FIX: prefer brigade_role over posture mission_type for sub-phase defaults */}
                            {renderSubPhasePanel(index, phase.duration_days, phase.brigade_role || phase.mission_type, false)}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Validation */}
              {phaseDaysValid && (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                  {STATUS_ICONS.GREEN_CHECK} All {operationDurationDays} days covered by phases.
                </Alert>
              )}
              {phaseDaysWarning && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                  {STATUS_ICONS.YELLOW_WARNING} Phases cover {totalPhaseDays} days, but
                  operation is {operationDurationDays} days. Add{' '}
                  {operationDurationDays - totalPhaseDays} more day(s).
                </Alert>
              )}
              {phaseDaysError && (
                <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
                  {STATUS_ICONS.RED_X} Phases total {totalPhaseDays} days, exceeding operation
                  duration ({operationDurationDays} days). Reduce by{' '}
                  {totalPhaseDays - operationDurationDays} day(s).
                </Alert>
              )}

              {/* Timeline Visualization */}
              {phases.length > 0 && (
                <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Timeline (Total: {operationDurationDays} days)
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                    {phases.map((phase, index) => (
                      <Box
                        key={index}
                        sx={{
                          flex: phase.duration_days,
                          height: 40,
                          bgcolor: `hsl(${index * 60}, 70%, 60%)`,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        P{phase.phase_number}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Help Text */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Phase Configuration Tips:</strong>
        </Typography>
        <Typography variant="body2" component="ul" sx={{ mb: 0, pl: 2 }}>
          <li>Phases define operational posture over time</li>
          <li>Total phase days must equal operation duration ({operationDurationDays} days)</li>
          <li>
            Inherited phases update automatically when parent changes — per-phase overrides are
            re-applied on top after each cascade
          </li>
          <li>
            Per-phase overrides allow e.g. a river crossing on Phase 2 while following division
            plan for all other phases
          </li>
          <li>Custom phases allow detached operations (e.g., screening force, reserve)</li>
        </Typography>
      </Alert>
    </Box>
  );
};

export default BrigadePhaseTab;

/**
 * INTEGRATION INSTRUCTIONS FOR Tab2_Operations.jsx:
 *
 * 1. Import this component at the top:
 *    import BrigadePhaseTab from './BrigadePhaseTab';
 *
 * 2. In your existing Brigade Dialog, add a new Tab:
 *    <Tabs value={tabValue} onChange={handleTabChange}>
 *      <Tab label="Basic Info" />
 *      <Tab label="Operation" />
 *      <Tab label="Environment" />
 *      <Tab label="Phases" />
 *    </Tabs>
 *
 * 3. In TabPanel content, add:
 *    <TabPanel value={tabValue} index={3}>
 *      <BrigadePhaseTab
 *        brigade={currentBrigade}
 *        divisionPhases={
 *          divisionConfigs.find(d => d.division_id === currentBrigade.parent_division_id)?.phases || []
 *        }
 *        divisionNumber={
 *          divisionConfigs.find(d => d.division_id === currentBrigade.parent_division_id)?.division_number || 1
 *        }
 *        operationDurationDays={totalDuration}
 *        onUpdateBrigade={(updatedBrigade) => {
 *          setBrigadeConfigs(prev => prev.map(b =>
 *            b.brigade_id === updatedBrigade.brigade_id ? updatedBrigade : b
 *          ));
 *        }}
 *        onToggleInheritance={toggleBrigadeInheritance}
 *      />
 *    </TabPanel>
 *
 * 4. ENH-023 (Tab2 v7.13.0 requirements):
 *    - Brigade init must include phase_overrides: {} at all init paths
 *    - getEffectivePhases(unit, parentPhases) module-level helper must exist
 *    - Cascade functions must call getEffectivePhases(brigade, newParentPhases)
 *      when updating brigade.phases so overrides survive re-cascades
 */
